import { useState } from "owa-react";
import { observer } from "owa-mobx-react";
import type { MailboxInfo } from "owa-client-types";
import { isSameCoprincipalAccountMailboxInfos } from "owa-client-types";
import DragAndDroppable from "owa-dnd/lib/components/DragAndDroppable";
import type { DragData } from "owa-dnd/lib/utils/dragDataUtil";
import { DraggableItemTypes } from "owa-dnd/lib/utils/DraggableItemTypes";
import folderStore, { isFolderLoaded, isSearchFolderExpired } from "owa-folders";
import { getCustomBundledIcon } from "owa-folders-common/lib/util/getCustomIconComponent";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import type { FolderForestTreeType, MailFolder } from "owa-graph-schema";
import toggleFolderNodeExpansion from "owa-mail-folder-store/lib/actions/toggleFolderNodeExpansion";
import { hideFolderTreeContextMenu } from "owa-mail-folder-store/lib/actions/folderTreeContextMenu";
import { getFolderViewStateFromIdNoFallback } from "owa-mail-folder-store/lib/selectors/getFolderViewStateFromId";
import type MailFolderDragData from "owa-mail-folder-store/lib/store/schema/MailFolderDragData";
import { getStore as getListViewStore, getSelectedTableView } from "owa-mail-list-store";
import { lazyMoveItemsBasedOnNodeIds, lazyCopyMailListRows, lazyMoveMailListRows, lazyMoveSingleItemFromItemId, } from "owa-mail-triage-action";
import type { MailListItemPartDragData } from "owa-mail-types/lib/types/MailListItemPartDragData";
import type { MailListRowDragData } from "owa-mail-types/lib/types/MailListRowDragData";
import { useComputedValue } from "owa-react-hooks/lib/useComputed";
import type { ChevronProps } from "owa-tree-node/lib/components/TreeNode";
import React from "react";
import { DRAG_X_OFFSET, DRAG_Y_OFFSET, TIME_INTERVAL_FOLDER_EXPAND, DRAG_MOVE_INTO_FOLDER_THRESHOLD, } from "../helpers/folderNodeDragConstants";
import { default as FolderOperationNode } from "./FolderOperationNode";
import { default as FolderNode } from "./FolderNode";
import { shouldRenderNodeInEditMode } from "./utils/shouldRenderNodeInEditMode";
import { dragHelperStyle, hiddenIconGutterAdjustment } from "./MailFolderNode.scss";
import { isAccountFeatureEnabled, isGlobalFeatureEnabled } from "owa-feature-flags";
import { getWin32FolderNodeIconProps } from "../utils/win32FolderIconProps";
import shouldShowAttachedInboxRulesDialog from "../helpers/shouldShowAttachedInboxRulesDialog";
import { SEARCH_FOLDER_ROOT_DISTINGUISHED_ID, SEARCH_FOLDER_TYPE_NAME, SHARED_FOLDERS_TREE_TYPE, ARCHIVE_FOLDERS_TREE_TYPE, isDragDropDisabledFolderType } from "owa-folders-constants";
import type DropEffect from "owa-dnd/lib/store/schema/DropEffect";
import { isCapabilityEnabled } from "owa-capabilities";
import { mailboxModificationCapability } from "owa-capabilities-definitions/lib/mailboxModificationCapability";
import { msgOftCapability } from "owa-capabilities-definitions/lib/msgOftCapability";
import { lazyMoveFolderPosition } from "owa-folders-move-folder-position";
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * Prefer Satchel for state management
 *	> 'useManagedMutation' import from 'owa-apollo-hooks' is restricted. Prefer Satchel for state management */
import { useManagedMutation } from "owa-apollo-hooks";
import { MoveFolderPositionDocument } from "owa-folders-move-folder-position/lib/graphql/__generated__/MoveFolderPositionMutation.interface";
import { getIsCustomSortEnabled } from "owa-folders/lib/selectors/getIsCustomSortEnabled";
import { type OpenFileMessageResponse, lazyOpenFileMessage, lazyPopoutFileMessage, lazyCreateCopyItem, lazyCreateCopyItemFailedNotification, FileOpenEntryPoint, checkIfDraftEml, } from "owa-mail-file-support";
import { isMoveCopyAllowed } from "owa-mail-triage-movecopy-importexport/lib/utils/copyMoveItemUtils";
import { type LocalFilesDragData } from "owa-dnd/lib/utils/LocalFilesDragData";
import { FolderPaneNavigationManager } from "owa-folders-nav";
import { logUsage } from "owa-analytics";
import { lazyLoadDraftToCompose } from "owa-mail-compose-actions";
import { type ComposeTarget } from "owa-mail-compose-store/lib/store/schema/ComposeTarget";
import type { ClientItemId } from "owa-client-ids";
import { getCustomFolderColorHexValue } from "owa-mail-folder-store/lib/selectors/getCustomFolderColor";
import { setDragStateForAddinsView } from "owa-dnd";
import isCrossAccountMovePolicyBlockedDrag from "../utils/isCrossAccountMovePolicyBlockedDrag";

export interface MailFolderNodeProps {
    depth: number; // root is 0, every sub node increase this number by 1.
    folderId: string;
    folder: MailFolder;
    treeType: FolderForestTreeType; // Type of folder tree
    isFolderExpandable: boolean;
    effectiveFolderDisplayName: string;
    distinguishedFolderParentIds?: string[];
    onContextMenu?: (evt: React.MouseEvent<unknown> | React.TouchEvent<unknown>, folderId: string, distinguishedFolderParentIds: string[]) => void;
    mailboxInfo: MailboxInfo;
    moveFolder: (destinationFolderId: string, destinationFolderMailboxInfo: MailboxInfo, sourceFolderId: string, sourceFolderMailboxInfo: MailboxInfo, sourceFolderParentFolderId: string, sourceFolderDisplayName: string) => void;
    deleteFolder: (folderId: string, isSearchFolder?: boolean, distinguishedFolderParentIds?: string[], resolveDialogImmediately?: boolean) => void;
    isBeingDragged?: boolean;
    shouldHideToggleFavorite?: boolean; // flag indicating whether to show toggle favorite option
    ellipsesOnHover?: boolean;
    folderNodeRef?: React.RefObject<HTMLDivElement | null>;
    parentRef?: React.RefObject<HTMLDivElement | null>;
}

const defaultExport: React.FunctionComponent<MailFolderNodeProps> = observer(function MailFolderNode(props: MailFolderNodeProps) {
    // Besides the isDragOver property in store, we also add this property here to distinguish from the dropping on FavoriteFolderNode
    const [isDragOver, setIsDragOver] = useState(false);
    const [isCrossAccountMoveBlockedDragOver, setIsCrossAccountMoveBlockedDragOver] = useState(false);
    
    const { folderId, treeType, depth, folder, isBeingDragged, mailboxInfo, effectiveFolderDisplayName, isFolderExpandable, onContextMenu, distinguishedFolderParentIds, moveFolder, ellipsesOnHover, folderNodeRef, parentRef, } = props;
    
    // State to keep track of when to show move-folder-position or move-folder-into treatment in UI
    const [isDragMoveInto, setisDragMoveInto] = useState(getIsCustomSortEnabled(mailboxInfo) ? false : true);
    
    const [moveFolderPositionMutation] = useManagedMutation(MoveFolderPositionDocument);
    
    const folderViewState = getFolderViewStateFromIdNoFallback(folderId);
    const { hideIcon, onRenderCustomIcon, chevronIconButtonClassName } = getWin32FolderNodeIconProps(isFolderExpandable);
    
    const renderInEditMode = useComputedValue((): boolean => {
        return shouldRenderNodeInEditMode(folderId, "rename", mailboxInfo.mailboxSmtpAddress, treeType);
    }, [folderId, mailboxInfo.mailboxSmtpAddress]);
    
    const renderAddNewNode = useComputedValue((): boolean => {
        return shouldRenderNodeInEditMode(folderId, "new", mailboxInfo.mailboxSmtpAddress, treeType);
    }, [folderId, mailboxInfo.mailboxSmtpAddress]);
    
    const renderAddNewOrEditNode = (nodeFolderId: string, nodeTreeType: FolderForestTreeType, nestDepth: number, operationType: string, originalValue?: string): React.JSX.Element => {
        return (<FolderOperationNode folderId={nodeFolderId} treeType={nodeTreeType} nestDepth={nestDepth} operationType={operationType} originalValue={originalValue} mailboxInfo={mailboxInfo}/>);
    };
    
    const onChevronClicked = React.useCallback((evt: React.MouseEvent<unknown> | KeyboardEvent) => {
        evt.stopPropagation();
        toggleFolderNodeExpansion(folderId);
        FolderPaneNavigationManager.resetSearchResults();
    }, [folderId]);
    
    const chevronProps = React.useMemo((): ChevronProps | undefined => {
        // Check if the node is expandable / should have chevrons
        if (isFolderExpandable) {
            return {
                isExpanded: !!folderViewState?.isExpanded,
                onClick: onChevronClicked,
                isAnimated: true,
                iconButtonClassName: chevronIconButtonClassName,
            };
        }
        else {
            return undefined;
        }
    }, [
        isFolderExpandable,
        folderViewState?.isExpanded,
        onChevronClicked,
        chevronIconButtonClassName,
    ]);
    
    const onContextMenu_0 = React.useCallback((evt: React.MouseEvent<unknown> | React.TouchEvent<unknown>) => {
        if (onContextMenu) {
            evt.stopPropagation();
            evt.preventDefault();
            onContextMenu(evt, folderId, distinguishedFolderParentIds ?? []);
        }
    }, [folderId, onContextMenu, distinguishedFolderParentIds]);
    
    const onDrop = React.useCallback(async (dragData: DragData, _pageX: number, _pageY: number, _currentTarget?: HTMLElement, ctrlKey?: boolean) => {
        const itemType = dragData.itemType;
        switch (itemType) {
            case DraggableItemTypes.MailListItemPart:
                {
                    const mailListItemPartDragData = dragData as MailListItemPartDragData;
                    const tableView = getListViewStore().tableViews.get(mailListItemPartDragData.tableViewId);
                    if (tableView?.id) {
                        const moveItemsBasedOnNodeIds = await lazyMoveItemsBasedOnNodeIds.import();
                        
                        moveItemsBasedOnNodeIds(mailListItemPartDragData.nodeIds, folderId, tableView.id, "Drag");
                    }
                }
                break;
            case DraggableItemTypes.MailListRow:
            case DraggableItemTypes.MultiMailListMessageRows:
            case DraggableItemTypes.MultiMailListConversationRows:
                const mailListRowDragData = dragData as MailListRowDragData;
                const tableViewId = mailListRowDragData.tableViewId;
                const rowKeys = mailListRowDragData.rowKeys;
                if (ctrlKey) {
                    const tableView = getListViewStore().tableViews.get(tableViewId);
                    if (tableView) {
                        lazyCopyMailListRows.importAndExecute(rowKeys, tableView, folderId, "Drag");
                    }
                }
                else {
                    lazyMoveMailListRows.importAndExecute(rowKeys, folderId, tableViewId, "Drag");
                }
                setDragStateForAddinsView(false);
                break;
            
            case DraggableItemTypes.MailFolderNode:
                {
                    const mailFolderItemBeingDragged = dragData as MailFolderDragData;
                    const draggedFolderId = mailFolderItemBeingDragged.folderId;
                    const targetFolder = folderStore().folderTable.get(draggedFolderId);
                    
                    if (targetFolder) {
                        const { mailboxInfo: draggedFolderMailboxInfo, parentFolderId: draggedFolderParentFolderId, } = targetFolder;
                        
                        if (isDragMoveInto) {
                            moveFolder(folderId /*destinationFolderId */, mailboxInfo /* destinationFolderMailboxInfo */, draggedFolderId /* sourceFolderId */, draggedFolderMailboxInfo /* sourceFolderMailboxInfo */, draggedFolderParentFolderId /* parentFolderId */, mailFolderItemBeingDragged.displayName);
                        }
                        else if (getIsCustomSortEnabled(mailboxInfo)) {
                            const moveFolderPositionCallback = () => {
                                lazyMoveFolderPosition.importAndExecute(moveFolderPositionMutation, {
                                    folderId: draggedFolderId,
                                    destinationFolderId: folderId,
                                    mailboxInfo,
                                });
                                
                                logUsage("FP_MoveFolderPos_DragMove", mailboxInfo);
                            };
                            
                            if (await shouldShowAttachedInboxRulesDialog(moveFolderPositionCallback, folderId, draggedFolderId, "DnD" /* actionSource */)) {
                                // If the dialog for attached inbox rules is shown, then the move operation will be handled by the dialog
                                return;
                            }
                            moveFolderPositionCallback();
                        }
                    }
                }
                break;
            case DraggableItemTypes.LocalFile:
                if (!isGlobalFeatureEnabled("mon-file-eml-msg") || !folderId) {
                    break;
                }
                const files = (dragData as LocalFilesDragData).files;
                const isPremiumUser = isCapabilityEnabled(msgOftCapability);
                const isDraftsFolder = folder.distinguishedFolderType === "drafts";
                
                for (const file of files) {
                    const fileExt = file.name.split(".").pop()?.toLowerCase();
                    // EML: allowed for all users, MSG/OFT: only for premium users
                    const canOpenFile = fileExt === "eml" ||
                        ((fileExt === "msg" || fileExt === "oft") && isPremiumUser);
                    
                    if (canOpenFile) {
                        // Check if EML is a draft by looking for x-unsent header
                        const isDraftEml = fileExt === "eml" && (await checkIfDraftEml(file));
                        
                        if (isDraftEml && isDraftsFolder) {
                            // Draft EML dropped on Drafts folder: opens as compose window
                            lazyOpenFileMessage
                                .importAndExecute(file, mailboxInfo)
                                .then((res: OpenFileMessageResponse) => {
                                // Client parser path opens compose automatically (isDraftOpenedAsNewMessage=true)
                                // Service parser path creates item but doesn't open compose, so we do it here
                                if (!res.isDraftOpenedAsNewMessage &&
                                    res.item?.ItemId) {
                                    const clientItemId: ClientItemId = {
                                        Id: res.item.ItemId.Id,
                                        mailboxInfo,
                                    };
                                    lazyLoadDraftToCompose.importAndExecute(clientItemId, null /* sxsId */, "FileMessage" /* actionSource */, 2);
                                }
                            })
                                .catch(() => { });
                        }
                        else {
                            // Non-draft EML, MSG/OFT, or draft EML on non-Drafts folder: just import and move
                            // Toast notification is shown by lazyMoveSingleItemFromItemId
                            lazyOpenFileMessage
                                .importAndExecute(file, mailboxInfo)
                                .then((res: OpenFileMessageResponse) => {
                                if (res.item?.ItemId && res.item?.ParentFolderId) {
                                    lazyMoveSingleItemFromItemId.importAndExecute(res.item.ItemId.Id, folderId);
                                }
                                else if (res.item) {
                                    lazyCreateCopyItem
                                        .importAndExecute(res.item, folderId, mailboxInfo)
                                        .catch(e => {
                                        lazyCreateCopyItemFailedNotification.importAndExecute(e.networkError);
                                    });
                                }
                            })
                                .catch(() => { });
                        }
                    }
                    else if (fileExt === "msg" || fileExt === "oft") {
                        // Non-premium user trying to open MSG/OFT: show premium error dialog
                        lazyPopoutFileMessage
                            .importAndExecute(file, FileOpenEntryPoint.DragDrop)
                            .catch(() => { });
                    }
                }
                break;
        }
    }, [folderId, moveFolder, mailboxInfo, isDragMoveInto, folder.distinguishedFolderType]);
    
    const onDragStart = React.useCallback(() => {
        hideFolderTreeContextMenu();
    }, []);
    
    /**
     * A callback function to check if current drag data is able to drop on current element
     * @param dragData the drag data which will be dropped
     * @param dragEvent the drag event to get the ctrlKey property value
     *
     * @returns the desired drop effect that will be used to update the cursor style
     */
    const canDrop = React.useCallback((dragData: DragData, dragEvent?: React.DragEvent<HTMLElement>): DropEffect => {
        if (isDragDropDisabledFolderType(folder.distinguishedFolderType)) {
            return "none";
        }
        
        const itemType = dragData.itemType;
        switch (itemType) {
            case DraggableItemTypes.MailListItemPart:
            case DraggableItemTypes.MailListRow:
            case DraggableItemTypes.MultiMailListMessageRows:
            case DraggableItemTypes.MultiMailListConversationRows: {
                const tableView = getSelectedTableView();
                let draggedItemsMailboxInfo;
                // Get source mailbox info from dragInfo if available
                // else get it from tableView
                if (dragData.itemData) {
                    const draggedItemData = dragData.itemData as {
                        mailboxInfo: MailboxInfo;
                    };
                    
                    draggedItemsMailboxInfo = draggedItemData?.mailboxInfo;
                }
                
                draggedItemsMailboxInfo =
                    draggedItemsMailboxInfo || getMailboxInfo(tableView);
                if (isMoveCopyAllowed(draggedItemsMailboxInfo, mailboxInfo)) {
                    return dragEvent?.ctrlKey ? "copy" : "move";
                }
                return "none";
            }
            
            case DraggableItemTypes.FavoriteNode: {
                if (dragData.itemData) {
                    const draggedItemData = dragData.itemData as {
                        mailboxInfo: MailboxInfo;
                    };
                    const favoriteMailboxInfo = draggedItemData?.mailboxInfo;
                    return isSameCoprincipalAccountMailboxInfos(favoriteMailboxInfo, mailboxInfo)
                        ? "move"
                        : "none";
                }
                else {
                    return "none";
                }
            }
            case DraggableItemTypes.MailFolderNode:
                if (dragData.itemData) {
                    const draggedItemData = dragData.itemData as {
                        mailboxInfo: MailboxInfo;
                        draggedFolderId: string;
                        draggedParentId: string;
                    };
                    
                    // disable moving distinguished folders into other folders
                    if (folderStore().folderTable.get(draggedItemData.draggedFolderId)
                        ?.distinguishedFolderType &&
                        (isDragMoveInto ||
                            (!isDragMoveInto &&
                                draggedItemData.draggedParentId &&
                                folder.parentFolderId &&
                                draggedItemData.draggedParentId !== folder.parentFolderId))) {
                        return "none";
                    }
                    
                    if (getIsCustomSortEnabled(mailboxInfo) &&
                        !isDragMoveInto &&
                        (isArchiveFolder || isSharedFolder)) {
                        return "none";
                    }
                    return isSameCoprincipalAccountMailboxInfos(draggedItemData.mailboxInfo, mailboxInfo)
                        ? "move"
                        : "none";
                }
                else {
                    return "none";
                }
            case DraggableItemTypes.LocalFile:
                return isGlobalFeatureEnabled("mon-file-eml-msg") ? "copy" : "none";
            default:
                return "none";
        }
    }, [folder, mailboxInfo, isDragMoveInto]);
    
    const onDragOver = React.useCallback((dragData: DragData) => {
        const isCrossAccountMoveBlocked = isCrossAccountMovePolicyBlockedDrag(dragData, mailboxInfo);
        setIsCrossAccountMoveBlockedDragOver(isCrossAccountMoveBlocked);
        if (getIsCustomSortEnabled(mailboxInfo)) {
            if (dragData.itemType !== DraggableItemTypes.MailFolderNode) {
                setisDragMoveInto(true);
            }
        }
        else {
            setisDragMoveInto(true);
        }
        setIsDragOver(true);
    }, [mailboxInfo]);
    
    const onDragEnter = React.useCallback(() => {
        if (getIsCustomSortEnabled(mailboxInfo)) {
            setisDragMoveInto(false);
        }
    }, [mailboxInfo]);
    
    const onDragLeave = React.useCallback(() => {
        setIsDragOver(false);
        setIsCrossAccountMoveBlockedDragOver(false);
    }, []);
    
    const canDrag = React.useCallback(() => {
        // we can only drag if it's not a shared folder, or the user has modify rights to a shared folder
        return treeType !== SHARED_FOLDERS_TREE_TYPE || !!folder.EffectiveRights?.Modify;
    }, [treeType, folder]);
    
    React.useEffect(() => {
        const timer = setTimeout(() => {
            if (isDragOver && isFolderExpandable && !folderViewState?.isExpanded) {
                toggleFolderNodeExpansion(folderId);
            }
            if (getIsCustomSortEnabled(mailboxInfo)) {
                setTimeout(() => setisDragMoveInto(isDragOver), DRAG_MOVE_INTO_FOLDER_THRESHOLD);
            }
        }, TIME_INTERVAL_FOLDER_EXPAND);
        return () => clearTimeout(timer);
    }, [isDragOver, mailboxInfo]);
    
    const getDragData = React.useCallback(() => {
        const folderNodeDragData: MailFolderDragData = {
            itemType: DraggableItemTypes.MailFolderNode,
            folderId,
            displayName: effectiveFolderDisplayName,
            treeType,
            mailboxInfo,
            parentFolderId: folder.parentFolderId,
            itemData: {
                treeType,
                mailboxInfo,
                draggedFolderId: folderId,
            },
        };
        return folderNodeDragData;
    }, [folderId, effectiveFolderDisplayName, mailboxInfo, folder, treeType]);
    
    const isSearchFolder = folder.distinguishedFolderType === SEARCH_FOLDER_ROOT_DISTINGUISHED_ID ||
        folder.type === SEARCH_FOLDER_TYPE_NAME ||
        (distinguishedFolderParentIds &&
            distinguishedFolderParentIds.indexOf(SEARCH_FOLDER_ROOT_DISTINGUISHED_ID) > -1);
    
    const isArchiveFolder = treeType === ARCHIVE_FOLDERS_TREE_TYPE;
    
    const isSharedFolder = treeType === SHARED_FOLDERS_TREE_TYPE;
    
    const isNewAndUnloadedSearchFolder = isSearchFolder && folder.totalMessageCount == 0 && !isFolderLoaded(folderId);
    
    const isReadOnlyMailbox = !isCapabilityEnabled(mailboxModificationCapability, mailboxInfo);
    
    const supportsDraggingOrDropping = !isSearchFolder /*Search folder nodes cannot be moved or have items dropped into them*/ &&
        (!isReadOnlyMailbox ||
            isAccountFeatureEnabled("acct-pstFileImportExport", mailboxInfo)); /*Read-only mailbox folders cannot be moved or have items dropped into them. Only allow if pst FileImportExport is ON */
    
    const deleteFolder = React.useCallback((resolveConfirmImmediately: boolean) => {
        // Only delete if it's not a shared folder, or the user has delete rights to the shared folder, and the mailbox is not read-only
        if ((treeType != SHARED_FOLDERS_TREE_TYPE || folder.EffectiveRights?.Delete) &&
            !isReadOnlyMailbox) {
            props.deleteFolder(folderId, isSearchFolder, distinguishedFolderParentIds, resolveConfirmImmediately);
        }
    }, [
        treeType,
        folder,
        props.deleteFolder,
        folderId,
        isSearchFolder,
        distinguishedFolderParentIds,
        isReadOnlyMailbox,
    ]);
    
    const customFolderColor = getCustomFolderColorHexValue(folderViewState?.color);
    
    const folderNodeElement = (<FolderNode chevronProps={chevronProps} hideIcon={hideIcon} customIconComponent={getCustomBundledIcon(folderId, false, isGlobalFeatureEnabled("fp-searchfolder-expiry") &&
            isSearchFolderExpired(folderId))} depth={depth} displayName={effectiveFolderDisplayName} folderId={folderId} isDroppedOver={folderViewState?.drop?.isDragOver && isDragOver} isBeingDragged={isBeingDragged} onContextMenu={onContextMenu_0} treeType={treeType} showHoverStateOnDroppedOver={isDragMoveInto} totalCount={folder.totalMessageCount} unreadCount={folder.UnreadCount} distinguishedFolderId={folder.distinguishedFolderType ?? ""} isLoading={isNewAndUnloadedSearchFolder} deleteFolder={deleteFolder} ellipsesOnHover={ellipsesOnHover} customIconColor={customFolderColor} parentRef={parentRef} nodeRef={folderNodeRef} customIconClassName={hideIcon ? hiddenIconGutterAdjustment : undefined} onRenderCustomIcon={onRenderCustomIcon} isCrossAccountMoveBlockedDragOver={isCrossAccountMoveBlockedDragOver}/>);
    
    return (<>
                {
        /**
         * Render edit node if renaming
         */
        renderInEditMode &&
            renderAddNewOrEditNode(folderId, treeType, depth, "rename", effectiveFolderDisplayName)}
                {
        /**
         * Render node if not editing
         */
        !renderInEditMode &&
            (!supportsDraggingOrDropping
                ? folderNodeElement
                : folderViewState?.drop && (<DragAndDroppable dragViewState={folderViewState?.drag} getDragData={getDragData} getDragPreview={getDragPreview} xOffset={DRAG_X_OFFSET} yOffset={DRAG_Y_OFFSET} onDragStart={onDragStart} dropViewState={folderViewState?.drop} onDrop={onDrop} canDrop={canDrop} onDragOver={onDragOver} onDragEnter={onDragEnter} onDragLeave={onDragLeave} canDrag={canDrag} preventBubblingDragEvents={true} shouldIgnoreTransientOnDragLeave={true}>
                                      {folderNodeElement}
                                  </DragAndDroppable>))}
                {
        /**
         * Render add new sub node
         */
        renderAddNewNode && renderAddNewOrEditNode(folderId, treeType, depth + 1, "new")}
            </>);
}, "MailFolderNode");
export default defaultExport;

function getDragPreview(folderNodeDragData: DragData) {
    const elem = document.createElement("div");
    elem.className = dragHelperStyle;
    elem.innerText = (folderNodeDragData as MailFolderDragData).displayName;
    return elem;
}
